import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { Grid } from '@mui/material';
import { SingleLineInput } from '@AMIEWEB/Unit/Common/SingleLineInput';
import { makeStyles } from 'tss-react/mui';
import { SimpleCheckbox } from '@AMIEWEB/Unit/Common/SimpleCheckbox';
import { getPositiveIntegerValue, validateWithinLastMonthValue } from '../helper';
import { CustomTooltip } from '@AMIEWEB/Common/Tooltips';

const useStyles = makeStyles()(theme => ({
  fieldStyle: {
    '& .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.grayChip} !important`,
    },
    '&:hover .MuiFilledInput-root': {
      backgroundColor: `${theme.palette.system.silver} !important`,
    },
    '&:hover .MuiInputLabel-root': {
      color: `${theme.palette.system.primary} !important`,
    },
  },
  checkbox: {
    '& label': {
      '& .MuiButtonBase-root.MuiCheckbox-root.Mui-checked': {
        color: `${theme.palette.system.main} !important`,
      },
      '&:hover .MuiButtonBase-root.MuiCheckbox-root': {
        color: `${theme.palette.system.main} !important`,
      },
    },
  },
}));

export const ReferencesContainer = () => {
  const { t } = useTranslation();
  const { control, watch, setValue, getValues } = useFormContext();
  const { classes } = useStyles();
  const [withinNumberOfMonthsValueHolder, setwithinNumberOfMonthsValueHolder] = useState(null);
  const numberOfReferenceToRFO = watch('reference.numberOfReferenceToRFO');
  const isRequired = watch('reference.isRequired');
  const withinNumberOfMonths = watch('reference.withinNumberOfMonths');
  const CONSTANT_MINUES_KEY = '-';
  const CONSTANT_DECIMAL_KEY = '.';

  useEffect(() => {
      if (Number(numberOfReferenceToRFO) < 1 && isRequired) {
        setValue('reference.isRequired', false, { shouldValidate: true });
      } else if (Number(numberOfReferenceToRFO) > 0 && !isRequired) {
        setValue('reference.isRequired', true, { shouldValidate: true });
      }
  }, [numberOfReferenceToRFO]);

  return (
    <Grid container columnSpacing={2}>
      <Grid item xs={4}>
        {/* # of References to Start */}
        <CustomTooltip placement="bottom" arrow={true} tooltipContent={t('facility.references.orderTooltipText')}>
          <Controller
            name={'reference.numberOfReferenceRequired'}
            control={control}
            render={({ field }) => (
              <SingleLineInput
                id={'numberOfReferenceRequired'}
                inputDefaultValue={getValues('reference.numberOfReferenceRequired')}
                inputLabel={t('facility.references.referencesRequired')}
                inputName={'numberOfReferenceRequired'}
                inputValue={field?.value}
                handleChange={e => {}}
                inputType={'number'}
                fieldStyle={classes.fieldStyle}
                shrinkLabel={true}
                isdisabled
              />
            )}
          />
        </CustomTooltip>
        {/* Wihtin Last Month */}
        <Grid item marginTop={3}>
          <Controller
            name={'reference.withinNumberOfMonths'}
            control={control}
            rules={{ validate: value => validateWithinLastMonthValue(value).isValid }}
            render={({ field }) => (
              <SingleLineInput
                id={'withinTheLast'}
                inputDefaultValue={null}
                inputLabel={t('facility.units.unitDetails.additionalInformation.references.withinTheLast')}
                inputName={'withinTheLast'}
                inputValue={withinNumberOfMonths}
                handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(event.target.value === '' ? null : event.target.value);
                }}
                showEndAdornment={true}
                endAdornmentValue={'months'}
                inputType={'number'}
                fieldStyle={classes.fieldStyle}
                isdisabled={watch('reference.withinMonths')}
                shrinkLabel={true}
                inputError={validateWithinLastMonthValue(field.value).isError}
                helperText={
                  validateWithinLastMonthValue(field.value).isError
                    ? t('facility.references.withinTheLastMonthError')
                    : ''
                }
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={4} marginRight={2}>
        {/* # of References to Submit */}
        <Controller
          name={'reference.numberOfReferenceToRFO'}
          control={control}
          render={({ field }) => (
            <SingleLineInput
              id={'numberOfReferences'}
              inputDefaultValue={getValues('reference.numberOfReferenceToRFO')}
              inputLabel={t('facility.references.referenceToSubmit')}
              inputName={'numberOfReferences'}
              inputValue={field.value}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                field.onChange(getPositiveIntegerValue(event.target.value));
              }}
              inputType={'number'}
              fieldStyle={classes.fieldStyle}
              shrinkLabel={true}
              onKeyPress={e => {
                if (e.key === CONSTANT_MINUES_KEY || e.key === CONSTANT_DECIMAL_KEY) {
                  e.preventDefault();
                }
              }}
            />
          )}
        />
        {/* Entire Work History */}
        <Grid item marginTop={3}>
          <Controller
            name={'reference.withinMonths'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  if (event.target.checked) {
                    setwithinNumberOfMonthsValueHolder(withinNumberOfMonths);
                    setValue('reference.withinNumberOfMonths', null, { shouldValidate: true });
                  } else {
                    setValue('reference.withinNumberOfMonths', withinNumberOfMonthsValueHolder, { shouldValidate: true });
                  }
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('facility.units.unitDetails.additionalInformation.references.entireTime')}
                checkboxName={'withinMonths'}
                id={'withinMonths'}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid item xs={3}>
        <Grid item>
          {/* Submission Requirement */}
          <Controller
            name={'reference.isRequired'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={watch('reference.isRequired')}
                handleChecked={event => {}}
                checkboxLabel={t('facility.units.unitDetails.additionalInformation.submissionRequirement')}
                checkboxName={'referenceRequired'}
                id={'referenceRequired'}
                isdisabled
              />
            )}
          />
        </Grid>
        <Grid item marginTop={3} className={classes.checkbox}>
          {/* Supervisor and above */}
          <Controller
            name={'reference.referenceFromSupervisor'}
            control={control}
            render={({ field }) => (
              <SimpleCheckbox
                isChecked={field.value || false}
                handleChecked={event => {
                  field.onChange(event.target.checked);
                }}
                checkboxLabel={t('facility.units.unitDetails.additionalInformation.references.supervisor')}
                checkboxName={'supervisorAndAbove'}
                id={'supervisorAndAbove'}
              />
            )}
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
